import { useEffect, useRef } from 'react'
import * as THREE from 'three'
import FOG from 'vanta/dist/vanta.fog.min'

import type { CSSProperties } from 'react'
import type { VantaFogEffect } from 'vanta/dist/vanta.fog.min'

/**
 * Volumetric fog background using Vanta.js + Three.js, tuned for the MC light
 * theme: a faint cool green-grey haze (white smoke would be invisible on the
 * pale background). Skips mounting entirely when the user prefers reduced
 * motion. Renders an absolutely positioned WebGL layer sized to its container.
 */
export default function VantaFog({
  className = '',
  style,
}: {
  className?: string
  style?: CSSProperties
}) {
  const containerRef = useRef<HTMLDivElement>(null)
  const effectRef = useRef<VantaFogEffect | null>(null)

  useEffect(() => {
    if (!containerRef.current) return

    const reduceMotion =
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (reduceMotion) return

    effectRef.current = FOG({
      el: containerRef.current,
      THREE: THREE,
      mouseControls: false,
      touchControls: false,
      gyroControls: false,
      minHeight: 200,
      minWidth: 200,
      // Faint green-grey light smoke (visible on MC's pale background).
      highlightColor: 0xe8efe6,
      midtoneColor: 0xcdd8c8,
      lowlightColor: 0xaebfa6,
      baseColor: 0xf2f5f0,
      blurFactor: 0.6,
      speed: 1.5,
      zoom: 0.8,
    })

    return () => {
      if (effectRef.current) {
        effectRef.current.destroy()
        effectRef.current = null
      }
    }
  }, [])

  return (
    <div
      ref={containerRef}
      className={className}
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 0,
        ...style,
      }}
      aria-hidden="true"
    />
  )
}
